import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  LogOut,
  Settings,
  Bell,
  Shield,
  HelpCircle,
  ChevronRight,
  Mail,
  Phone,
  Building2,
  BadgeCheck,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ROLE_LABELS } from "../../constants/config";

const MenuItem = ({ icon: Icon, label, sub, onClick, testId, danger = false }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className="w-full flex items-center gap-3 p-3.5 hover:bg-honda-bg transition-colors text-left border-b border-honda-border last:border-0"
  >
    <div
      className={`w-9 h-9 rounded-lg flex items-center justify-center ${
        danger ? "bg-red-100 text-red-600" : "bg-honda-bg text-honda-ink"
      }`}
    >
      <Icon size={16} />
    </div>
    <div className="flex-1">
      <p className={`text-sm font-medium ${danger ? "text-red-600" : "text-honda-ink"}`}>
        {label}
      </p>
      {sub && <p className="text-[11px] text-honda-muted mt-0.5">{sub}</p>}
    </div>
    <ChevronRight size={16} className="text-honda-subtle" />
  </button>
);

import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      return toast.error("Ukuran foto maksimal 2MB");
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const base64Data = event.target.result;
        const { data } = await api.patch(`/auth/me`, { avatar: base64Data });
        setUser(data);
        localStorage.setItem("hscis_user", JSON.stringify(data));
        toast.success("Foto profil berhasil diperbarui");
      } catch (err) {
        toast.error("Gagal mengupdate foto profil");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const initials = (user?.name || "U").split(" ").slice(-1)[0][0].toUpperCase();

  return (
    <div data-testid="profile-page" className="pb-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-honda-red to-[#9F140E] text-white px-5 pt-12 pb-16 relative">
        <button
          data-testid="profile-back-button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center hover:bg-white/25 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="mt-5 flex items-center gap-4">
          <div className="relative flex-shrink-0 cursor-pointer group" onClick={handleAvatarClick}>
            <input 
              type="file" 
              ref={fileInputRef} 
              className="hidden" 
              accept="image/*" 
              onChange={handleFileChange}
            />
            {user?.avatar ? (
              <img
                src={user?.avatar}
                alt={user?.name}
                className={`w-20 h-20 rounded-2xl border-4 border-white/30 object-cover bg-white/10 ${uploading ? 'opacity-50' : ''}`}
              />
            ) : (
              <div className={`w-20 h-20 rounded-2xl border-4 border-white/30 bg-white/20 flex items-center justify-center font-heading font-bold text-3xl ${uploading ? 'opacity-50' : ''}`}>
                {initials}
              </div>
            )}
            
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
              <BadgeCheck size={14} className="text-honda-red" />
            </div>
            <div className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
               <span className="text-[10px] font-bold">Ubah</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-extrabold text-xl leading-tight truncate">{user?.name}</h1>
            <span className="inline-block mt-1 text-[10px] uppercase tracking-wider font-bold bg-white/20 backdrop-blur px-2 py-0.5 rounded-full">
              {ROLE_LABELS[user?.role]}
            </span>
            <p className="text-xs text-white/85 mt-1 truncate">NIP {user?.nip}</p>
          </div>
        </div>
      </div>

      {/* Info card */}
      <div className="px-5 -mt-10">
        <div className="honda-card overflow-hidden shadow-lg">
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2.5">
              <Mail size={14} className="text-honda-subtle" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-honda-subtle font-semibold">
                  Email
                </p>
                <p className="text-sm text-honda-ink">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Phone size={14} className="text-honda-subtle" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-honda-subtle font-semibold">
                  Telepon
                </p>
                <p className="text-sm text-honda-ink">{user?.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Building2 size={14} className="text-honda-subtle" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-honda-subtle font-semibold">
                  Dealer
                </p>
                <p className="text-sm text-honda-ink">{user?.dealer}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Menu */}
      <div className="px-5 mt-5">
        <p className="honda-label">Pengaturan</p>
        <div className="mt-2 honda-card overflow-hidden">
          <MenuItem icon={Bell} label="Notifikasi" sub="Push, email, SMS" testId="menu-notifications" />
          <MenuItem icon={Settings} label="Preferensi" sub="Bahasa, tema, GPS" testId="menu-settings" />
          <MenuItem icon={Shield} label="Keamanan" sub="Ubah password, PIN" testId="menu-security" />
        </div>

        <p className="honda-label mt-5">Bantuan</p>
        <div className="mt-2 honda-card overflow-hidden">
          <MenuItem icon={HelpCircle} label="Pusat Bantuan" sub="FAQ, kontak support" testId="menu-help" />
        </div>

        <button
          data-testid="profile-logout-button"
          onClick={handleLogout}
          className="w-full mt-5 honda-card p-4 flex items-center justify-center gap-2 text-red-600 font-semibold hover:bg-red-50 transition-colors"
        >
          <LogOut size={16} /> Keluar dari Akun
        </button>

        <p className="text-center text-[11px] text-honda-subtle mt-4">
          SALES TRACK v1.0.0 · Honda Babel © 2026
        </p>
      </div>
    </div>
  );
}
